import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

const OrganismeContext = createContext(null);

export const OrganismeProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organismeActif, setOrganismeActif] = useState(null);
  const [isSuperAdminMode, setIsSuperAdminMode] = useState(false);

  // Charger la préférence depuis localStorage au montage
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'super_admin')) {
      const savedOrganisme = localStorage.getItem('organismeActif');
      const savedMode = localStorage.getItem('isSuperAdminMode');
      
      // Si super admin, vérifier le mode sauvegardé
      if (user.role === 'super_admin' && savedMode === 'true') {
        setIsSuperAdminMode(true);
        setOrganismeActif(null);
      } else {
        setIsSuperAdminMode(false);
        // Charger l'organisme sauvegardé ou le premier par défaut
        const organismes = user.organismes || [];
        if (organismes.length > 0) {
          const defaultOrganisme = savedOrganisme && organismes.includes(savedOrganisme) 
            ? savedOrganisme 
            : organismes[0];
          setOrganismeActif(defaultOrganisme);
          localStorage.setItem('organismeActif', defaultOrganisme);
        }
      }
    }
  }, [user]);

  // Changer d'organisme
  const switchOrganisme = (organisme) => {
    setOrganismeActif(organisme);
    setIsSuperAdminMode(false);
    localStorage.setItem('organismeActif', organisme);
    localStorage.setItem('isSuperAdminMode', 'false');
    
    // Rediriger vers le dashboard de l'organisme
    navigate('/admin/dashboard');
  };

  // Activer le mode Super Admin
  const enableSuperAdminMode = () => {
    setIsSuperAdminMode(true);
    setOrganismeActif(null);
    localStorage.setItem('isSuperAdminMode', 'true');
    
    // Rediriger vers la page "tous les utilisateurs" en mode super admin
    navigate('/super-admin/all-users');
  };

  // Désactiver le mode Super Admin (retour au mode admin organisme)
  const disableSuperAdminMode = () => {
    setIsSuperAdminMode(false);
    const organismes = user?.organismes || [];
    if (organismes.length > 0) {
      const defaultOrganisme = organismes[0];
      setOrganismeActif(defaultOrganisme);
      localStorage.setItem('organismeActif', defaultOrganisme);
    }
    localStorage.setItem('isSuperAdminMode', 'false');
    
    // Rediriger vers le dashboard de l'organisme #0
    navigate('/admin/dashboard');
  };

  // Vérifier si l'utilisateur a accès à plusieurs organismes
  const hasMultipleOrganismes = () => {
    return user?.organismes && user.organismes.length > 1;
  };

  // Vérifier si l'utilisateur est super admin
  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  return (
    <OrganismeContext.Provider
      value={{
        organismeActif,
        isSuperAdminMode,
        switchOrganisme,
        enableSuperAdminMode,
        disableSuperAdminMode,
        hasMultipleOrganismes,
        isSuperAdmin,
        organismes: user?.organismes || [],
      }}
    >
      {children}
    </OrganismeContext.Provider>
  );
};

export const useOrganisme = () => {
  const context = useContext(OrganismeContext);
  if (!context) {
    throw new Error('useOrganisme must be used within an OrganismeProvider');
  }
  return context;
};
